import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { MarketingService } from '@/services/marketing.service';
import { format } from 'date-fns';

export const ContentManagement: React.FC = () => {
  const { profile } = useAuth();
  const [filter, setFilter] = useState<'all' | 'draft' | 'scheduled' | 'published' | 'archived'>('all');

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['contentPosts', filter],
    queryFn: async () => {
      const all = await MarketingService.getAllContentPosts();
      if (filter === 'all') return all;
      return all.filter(p => p.status === filter);
    },
    enabled: !!profile,
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const totalPosts = posts.length;
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  const scheduledPosts = posts.filter(p => p.status === 'scheduled').length;
  const draftPosts = posts.filter(p => p.status === 'draft').length;

  // Calculate total engagement
  const totalViews = posts.reduce((sum, p) => sum + (p.metrics?.views || 0), 0);
  const totalLikes = posts.reduce((sum, p) => sum + (p.metrics?.likes || 0), 0);
  const totalShares = posts.reduce((sum, p) => sum + (p.metrics?.shares || 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Content Management</h2>
        <Link
          to="/marketing/content/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
        >
          + Create Content
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Posts</div>
          <div className="text-2xl font-bold text-gray-900">{totalPosts}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Published</div>
          <div className="text-2xl font-bold text-green-600">{publishedPosts}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Scheduled</div>
          <div className="text-2xl font-bold text-blue-600">{scheduledPosts}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Drafts</div>
          <div className="text-2xl font-bold text-gray-600">{draftPosts}</div>
        </div>
      </div>

      {/* Engagement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Views</div>
          <div className="text-2xl font-bold text-purple-600">{totalViews.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Likes</div>
          <div className="text-2xl font-bold text-pink-600">{totalLikes.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Shares</div>
          <div className="text-2xl font-bold text-indigo-600">{totalShares.toLocaleString()}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex space-x-2">
        {(['all', 'draft', 'scheduled', 'published', 'archived'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              filter === status
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading content...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No content found</p>
          <Link
            to="/marketing/content/new"
            className="mt-4 inline-block text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Create your first content post
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {posts.map((post) => (
              <li key={post.id}>
                <Link
                  to={`/marketing/content/${post.id}`}
                  className="block hover:bg-gray-50 px-6 py-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {post.title}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                          {post.status}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 capitalize">
                          {post.type}
                        </span>
                        {post.platform && (
                          <span className="ml-2 text-xs text-gray-500">
                            {post.platform}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {post.content && post.content.length > 100
                          ? `${post.content.substring(0, 100)}...`
                          : post.content}
                        {post.publishedAt && ` • Published: ${format(post.publishedAt, 'MMM d, yyyy')}`}
                        {post.scheduledAt && !post.publishedAt && ` • Scheduled: ${format(post.scheduledAt, 'MMM d, yyyy')}`}
                        {post.authorName && ` • By ${post.authorName}`}
                      </p>
                      {post.metrics && (
                        <div className="mt-2 flex space-x-4 text-xs text-gray-500">
                          {post.metrics.views !== undefined && post.metrics.views > 0 && (
                            <span>👁 {post.metrics.views} views</span>
                          )}
                          {post.metrics.likes !== undefined && post.metrics.likes > 0 && (
                            <span>❤ {post.metrics.likes} likes</span>
                          )}
                          {post.metrics.shares !== undefined && post.metrics.shares > 0 && (
                            <span>↗ {post.metrics.shares} shares</span>
                          )}
                          {post.metrics.comments !== undefined && post.metrics.comments > 0 && (
                            <span>💬 {post.metrics.comments} comments</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {post.url && (
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          View →
                        </a>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

